import { integrationEhrLaunchAtom } from '@/atoms/integrationEhrLaunchAtoms';
import { integrationMapping } from '@/helpers/integrationMapping';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import validJwtToken from '@/utils/validJwtToken';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

export const useValidateEpicLaunch = () => {
  const ehrLaunch = useAtomValue(integrationEhrLaunchAtom);
  const isFhirAuth = useIsFhirAuth();

  const sessionId = useCurrentSessionId();
  const { data: sessionData } = useQuerySessionById({
    sessionId,
    select: (data) => ({
      ehr_provider: (data?.ehr_provider ?? data?.patient_profile?.ehr) as EHRProvider,
      encounter_id: data?.ehr_encounter_id,
    }),
  });

  const isEpicLaunch = sessionData?.ehr_provider
    ? integrationMapping[sessionData.ehr_provider]?.ehrLaunch
    : false;
  const isEpicLaunchTokenValid =
    !!ehrLaunch?.accessToken && validJwtToken(ehrLaunch.accessToken);

  const isSameEncounter =
    sessionData?.encounter_id && ehrLaunch?.ehrLaunchInfo?.encounterId
      ? sessionData.encounter_id === ehrLaunch.ehrLaunchInfo.encounterId
      : false;

  // isSamePatient check intentionally dropped. Epic returns two different FHIR ID
  // formats for the same patient:
  //   - ~24-char R4 ID (e.g. `eJFAy...`) from the SMART-on-FHIR web launch
  //   - ~46-char DSTU2/opaque ID (e.g. `TJrpJf...`) from the Haiku mobile FHIR endpoint
  // Both identify the same patient but never string-match. Encounter is a stronger
  // uniqueness guarantee for launch-context validation, so we rely on isSameEncounter.

  return useMemo(
    () => ({
      isSameEncounter,
      isEpicLaunch,
      isEpicLaunchTokenValid,
      isValidEpicLaunch:
        isEpicLaunch && isSameEncounter && (isEpicLaunchTokenValid || isFhirAuth),
    }),
    [isEpicLaunch, isEpicLaunchTokenValid, isFhirAuth, isSameEncounter]
  );
};
